import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/auth-context.jsx';
import { adminApi } from '../../lib/admin-api/index.js';
import PageHeader from '../../shared/ui/page-header/page-header.jsx';

function RecoveryCodes({ codes }) {
  if (!codes?.length) {
    return null;
  }

  return (
    <div className="alert alert-warning mt-3 mb-0">
      <div className="fw-semibold mb-2">Save these recovery codes now. They will not be shown again.</div>
      <div className="row g-2">
        {codes.map((code) => (
          <div className="col-sm-6" key={code}>
            <code className="d-block bg-white text-dark rounded px-2 py-1">{code}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecurityPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const refreshStatus = async () => {
    const nextStatus = await adminApi.getTwoFactorStatus(token);
    setStatus(nextStatus);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const nextStatus = await adminApi.getTwoFactorStatus(token);
        if (!cancelled) {
          setStatus(nextStatus);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Failed to load security settings');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const startSetup = async () => {
    setError('');
    setSuccess('');
    setRecoveryCodes([]);
    setIsSaving(true);
    try {
      setSetup(await adminApi.setupTwoFactor(token));
    } catch (requestError) {
      setError(requestError.message || 'Failed to start setup');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmSetup = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      const response = await adminApi.confirmTwoFactor(token, confirmCode);
      setRecoveryCodes(response?.recoveryCodes || []);
      setSetup(null);
      setConfirmCode('');
      setSuccess('Two-factor authentication is enabled.');
      await refreshStatus();
    } catch (requestError) {
      setError(requestError.message || 'Invalid authenticator code');
    } finally {
      setIsSaving(false);
    }
  };

  const disableTwoFactor = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      await adminApi.disableTwoFactor(token, disablePassword, disableCode);
      setDisablePassword('');
      setDisableCode('');
      setSuccess('Two-factor authentication is disabled.');
      await refreshStatus();
    } catch (requestError) {
      setError(requestError.message || 'Failed to disable two-factor authentication');
    } finally {
      setIsSaving(false);
    }
  };

  const regenerateCodes = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      const response = await adminApi.regenerateTwoFactorRecoveryCodes(token, disablePassword, disableCode);
      setRecoveryCodes(response?.recoveryCodes || []);
      setSuccess('Recovery codes regenerated.');
      await refreshStatus();
    } catch (requestError) {
      setError(requestError.message || 'Failed to regenerate recovery codes');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="card"><div className="card-body">Loading security settings...</div></div>;
  }

  return (
    <>
      <PageHeader title="Security" subtitle="Authenticator app and recovery code controls" />
      <div className="card">
        <div className="card-body">
          {error ? <div className="alert alert-danger">{error}</div> : null}
          {success ? <div className="alert alert-success">{success}</div> : null}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h5 className="mb-1">Two-factor authentication</h5>
              <div className="text-muted">
                Status: {status?.enabled ? 'Enabled' : 'Disabled'}
                {status?.recoveryCodesRemaining ? `, ${status.recoveryCodesRemaining} recovery codes remaining` : ''}
              </div>
            </div>
            {!status?.enabled ? (
              <button type="button" className="btn btn-primary" onClick={startSetup} disabled={isSaving}>
                Enable 2FA
              </button>
            ) : null}
          </div>

          {setup ? (
            <form onSubmit={confirmSetup} className="border rounded p-3 mb-4">
              {setup.otpauthUri ? (
                <div className="mb-3">
                  <label className="form-label">Authenticator QR code</label>
                  <div className="d-flex flex-column flex-sm-row align-items-start gap-3">
                    <div className="bg-white rounded p-2">
                      <QRCodeSVG
                        value={setup.otpauthUri}
                        size={180}
                        level="M"
                        marginSize={2}
                        title="Authenticator app QR code"
                        aria-label="Authenticator app QR code"
                        role="img"
                      />
                    </div>
                    <div className="form-text mt-sm-1">
                      Scan this code with Google Authenticator, Authy, 1Password, or another TOTP app.
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="mb-3">
                <label className="form-label">Authenticator secret</label>
                <input className="form-control font-monospace" value={setup.secret || ''} readOnly />
                <div className="form-text">Add this key to Google Authenticator, Authy, 1Password, or another TOTP app.</div>
                {setup.otpauthUri ? (
                  <a className="small d-inline-block mt-2" href={setup.otpauthUri}>Open authenticator setup URI</a>
                ) : null}
              </div>
              <div className="mb-3">
                <label className="form-label">6-digit code</label>
                <input className="form-control" value={confirmCode} onChange={(event) => setConfirmCode(event.target.value)} required />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>Confirm and enable</button>
            </form>
          ) : null}

          {status?.enabled ? (
            <form onSubmit={disableTwoFactor} className="border rounded p-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Current password</label>
                  <input type="password" className="form-control" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Authenticator or recovery code</label>
                  <input className="form-control" value={disableCode} onChange={(event) => setDisableCode(event.target.value)} required />
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-outline-danger" disabled={isSaving}>Disable 2FA</button>
                <button type="button" className="btn btn-outline-secondary" onClick={regenerateCodes} disabled={isSaving}>Regenerate recovery codes</button>
              </div>
            </form>
          ) : null}
          <RecoveryCodes codes={recoveryCodes} />
        </div>
      </div>
    </>
  );
}

export default SecurityPage;
